// CompetitionStatsSection — the Stats leaderboard collection (UI-SPEC §9):
// rating head board (data-permitting, K4), the nine category boards and the
// three team boards, all as configured CompetitionLeaderboardCard instances.
// Self-contained, props only.
//
// K4 rule: without a season rating source the rating board (and nothing
// else) is absent — the Goals board becomes the first board of the grid and
// no layout gap is left behind. Boards without provider data are absent, not
// empty (§9.2 verbatim: "BOARDS WITHOUT PROVIDER DATA ARE ABSENT · GK BOARD
// LISTS GOALKEEPERS ONLY" — the GK filter is a DATA responsibility of the
// caller/BE, this component renders the rows it is given).
//
// Column rule (§9.2 / §24): 2 boards per row at 1440, 3 at ≥1920, single
// column on mobile — keyed off the CONTAINER width (embeddable).
import React from "react";
import CompetitionLeaderboardCard from "./CompetitionLeaderboardCard";
import CompetitionRatingBoard from "./CompetitionRatingBoard";
import { C, FONT_FAMILY } from "./shared/competitionsUi";
import useCompetitionsTr from "./shared/useCompetitionsTr";
import useContainerWidth from "./shared/useContainerWidth";

// V1 board set + order, verbatim from UI-SPEC §9.2 (titles + units are
// translation keys; the set stays configurable via the data that arrives —
// a board with no rows simply does not render).
export const PLAYER_BOARDS = [
  { key: "goals", title: "Goals", unit: "Goals" },
  { key: "assists", title: "Assists", unit: "Assists" },
  { key: "keyPasses", title: "Key passes", unit: "KP" },
  { key: "bigChancesCreated", title: "Big chances created", unit: "BCC" },
  { key: "dribbles", title: "Dribbles (succ.)", unit: "Succ." },
  { key: "tackles", title: "Tackles", unit: "Tkl" },
  { key: "interceptions", title: "Interceptions", unit: "Int" },
  { key: "duelsWon", title: "Duels won", unit: "Duels" },
  { key: "gkSaves", title: "Goalkeepers · saves", unit: "Saves" },
];

export const TEAM_BOARDS = [
  { key: "bestAttack", title: "Best attack", unit: "Goals for" },
  { key: "bestDefence", title: "Best defence", unit: "Goals against" },
  { key: "possession", title: "Possession", unit: "Ø share" },
];

const CompetitionStatsSection = ({
  rating, // { rows, minutesShareLabel? } | null/undefined -> board absent (K4)
  playerBoards = {}, // { goals: rows[], assists: rows[], ... } per PLAYER_BOARDS keys
  teamBoards = {}, // { bestAttack: rows[], bestDefence: rows[], possession: rows[] }
  renderAction,
  onNavigate,
  navigable,
  title,
}) => {
  const { tr } = useCompetitionsTr();
  const [containerRef, width] = useContainerWidth(1024);

  const ratingRows = (rating && rating.rows) || [];
  const activePlayerBoards = PLAYER_BOARDS.filter(
    (board) => (playerBoards[board.key] || []).length > 0,
  );
  const activeTeamBoards = TEAM_BOARDS.filter(
    (board) => (teamBoards[board.key] || []).length > 0,
  );

  // "Empty renders nothing" — the whole section is absent without any data.
  if (!ratingRows.length && !activePlayerBoards.length && !activeTeamBoards.length) {
    return null;
  }

  // §9.2/§24: 3 board columns "AT ≥1920" (viewport). The REAL container at a
  // 1920 viewport is 1920 − 2×72px page padding = 1776px — minus a classic
  // Windows scrollbar ≈ 1759px. Threshold 1750 keeps 1440 (container ≈1296)
  // safely at 2 columns while 1920 genuinely gets 3 (QA-CODE §4.11).
  const playerCols = width < 640 ? 1 : width >= 1750 ? 3 : 2;
  const teamCols = width < 640 ? 1 : 3;

  return (
    <section
      ref={containerRef}
      style={{ fontFamily: FONT_FAMILY, display: "flex", flexDirection: "column", gap: 18 }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span
          style={{
            fontSize: width < 640 ? 16 : 19,
            fontWeight: 700,
            color: C.ink900,
            letterSpacing: "-.02em",
          }}
        >
          {title || tr("Stats")}
        </span>
        <span style={{ flex: 1, height: 1, background: C.grid }} />
      </div>
      {ratingRows.length > 0 && (
        <CompetitionRatingBoard
          rows={ratingRows}
          minutesShareLabel={rating && rating.minutesShareLabel}
          renderAction={renderAction}
          onNavigate={onNavigate}
          navigable={navigable}
        />
      )}
      {activePlayerBoards.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${playerCols}, minmax(0,1fr))`,
            gap: 20,
            alignItems: "start",
          }}
        >
          {activePlayerBoards.map((board) => (
            <CompetitionLeaderboardCard
              key={board.key}
              title={tr(board.title)}
              unit={tr(board.unit)}
              rows={playerBoards[board.key]}
              kind="player"
              renderAction={renderAction}
              onNavigate={onNavigate}
              navigable={navigable}
            />
          ))}
        </div>
      )}
      {activeTeamBoards.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${teamCols}, minmax(0,1fr))`,
            gap: 20,
            alignItems: "start",
          }}
        >
          {activeTeamBoards.map((board) => (
            <CompetitionLeaderboardCard
              key={board.key}
              title={tr(board.title)}
              unit={tr(board.unit)}
              rows={teamBoards[board.key]}
              kind="team"
              renderAction={renderAction}
              onNavigate={onNavigate}
              navigable={navigable}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default CompetitionStatsSection;
